import { useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import Footer from "../../Structure/Footer/Footer";
import Header from "../Header/Header";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { useUser } from "../../../contexts/userContext/userContext.js";
import useFetch from "../../../hooks/useFecth.ts";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import { AddGoogleAnalyticsPageView } from "../../../utils/googleAnalyticsHandler";
import AccessibilityWidget from "../Page/AccessibilityWidget";
import Cookies from "universal-cookie";
import config from "config";
import PopUpNotifications from "../../Reusable/Notifications/PopUpNotifications/PopUpNotifications";
import Image from "../../Elements/Image/Image.tsx";
import BlackFridayHeader from "components/Reusable/BlackFriday/BlackFridayHeader";
import { getLocalStorage } from "utils/globalLocalStorage";
import Announcement from "components/Reusable/Announcement/Announcement.js";
import PromoContactUsFloatingButton from "components/Reusable/PromoContactUsFloatingButton";
import CustomDomainFooter from "../CustomDomainFooter/index.tsx";
import isCustomDomainPage from "../../../helpers/isCustomDomainPage.ts";
import { CustomDomainPagesE } from "../../../models/enums.ts";
import checkCurrDomainIsOrgDomain from "../../../helpers/checkCurrDomainIsOrgDomain.ts";

const RouterHelper = ({ support, children, withoutUIWrapper }) => {
  const {
    fetchConsts,
    fetchGlobalConsts,
    fetchSupportCategories,
    fetchSupportQuestionsList,
    fetchSupportSingleQuestion,
    fetchAndSetMetaData,
    getDirection,
    getLanguage,
    setCurrentPage,
    fetchAndSetBannersData,
    isGoogleAnalyticsInitialized,
    getPath,
    getLanguages,
    setLanguage,
    getTextByKey,
  } = useContent();

  const params = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();

  const [showPreloader, setShowPreloader] = useState(true);

  const currentPage = useMemo(
    () =>
      pathname.includes("/user/")
        ? pathname.split("/user/")[1].split("/")[1]
        : pathname.includes(`/${getLanguage()}/`) &&
          pathname.split(`/${getLanguage()}/`)[1].split("/")[0],
    []
  );

  useEffect(() => {
    if (currentPage === "careers") {
      window.location.href = config.baseurl + getPath();
    }
  }, [currentPage]);

  //fetching marketing logic and data
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    setCurrentPage(currentPage);
  }, []);

  useEffect(() => {
    fetchAndSetBannersData(currentPage, window.innerWidth < 993);
  }, [currentPage]);

  // fetching data (consts,metadata,support)
  const [isMetaDataReturned] = useFetch(
    {
      fetchFunction: fetchAndSetMetaData,
      dependency: currentPage,
    },
    currentPage
  );
  const [isGlobalConstsReturned] = useFetch({
    fetchFunction: fetchGlobalConsts,
  });
  const [isPageConstsReturned] = useFetch({ fetchFunction: fetchConsts });
  const [SupportCategoriesReturned] = useFetch({
    fetchFunction: fetchSupportCategories,
  });
  const [questionsListReturned] = useFetch(
    {
      fetchFunction: fetchSupportQuestionsList,
    },
    params.supportCategory
  );
  const [supportQuestionReturned] = useFetch(
    {
      fetchFunction: fetchSupportSingleQuestion,
    },
    params.supportQuestion
  );

  const isPageReadyToShowContent =
    isMetaDataReturned &&
    isGlobalConstsReturned &&
    isPageConstsReturned &&
    SupportCategoriesReturned &&
    questionsListReturned &&
    supportQuestionReturned;

  useEffect(() => {
    if (isPageReadyToShowContent) {
      const customEvent = new CustomEvent("on-page-ready");
      document.dispatchEvent(customEvent);
    }
  }, [isPageReadyToShowContent]);

  const handleChangeLang = (lang: string) => {
    const betweenSlashArray = pathname.split("/");
    betweenSlashArray[1] = lang;
    const newPath = betweenSlashArray.join("/");
    navigate(newPath);
    setLanguage(lang);
    // window.location.reload(false);
  };

  useLayoutEffect(() => {
    const langs = Object.keys(getLanguages()).join("|");
    if (params.lang && !langs.includes(params.lang)) {
      const lang =
        getLocalStorage("lang") ||
        navigator.language?.split("-")[0] ||
        navigator.language?.split("-")[0] ||
        "en";

      if (!langs.includes(lang)) {
        handleChangeLang("en");
      } else {
        handleChangeLang(lang);
      }
    }
  }, [params.lang]);

  useMemo(() => {
    // add Google Analytics for this pageview
    AddGoogleAnalyticsPageView({ isGoogleAnalyticsInitialized });
  }, []);

  // setting direction as ref-value
  const direction = useRef(getDirection());

  useMemo(() => {
    //replacing ref-value if direction was changed
    setTimeout(() => {
      direction.current = getDirection();
    }, 300);
  }, [getDirection()]);

  const directionClassName = direction.current
    ? direction.current
    : getLanguage() === "he" || getLanguage() === "ar"
    ? "rtl"
    : "ltr";
  //5.returning the component to show > transition is for fade in only. not fade-out

  const hideWhenCollection = window.location.pathname.includes("collection")
    ? (window.location.hostname &&
        !window.location.hostname.includes("jumbomail")) ||
      !window.location.hostname
    : false;

  useMemo(() => {
    const browser = navigator.userAgent.toLowerCase();
    const isIE = browser.indexOf("msie") !== -1;
    const isIE10 = browser.split("msie");
    if (
      browser.indexOf("msie") !== -1 &&
      isIE10.length > 0 &&
      isIE10[1] &&
      parseInt(isIE10[1]) <= 9 &&
      pathname.indexOf("error/unsupported-browser ") < 0
    )
      window.location.pathname = "l" + getPath() + "error/unsupported-browser";
  }, []);

  //check if exist test cookies
  useMemo(() => {
    const cookies = new Cookies();
    const isTokenExist = cookies.get("Test-testenvironment");
    if (
      window.location.href.includes("test.jumbomail") ||
      window.location.href.includes("test2.jumbomail")
    ) {
      if (!isTokenExist) {
        window.location.href = `${window.location.origin}/l/en/authenticate-user`;
      }
    }
  }, []);

  // BLACK FRIDAY PROMO
  // useBlackFridayUser();
  // BLACK FRIDAY PROMO
  return withoutUIWrapper ? (
    children
  ) : (
    <>
      <CSSTransition
        in={showPreloader}
        timeout={300}
        classNames="fade"
        unmountOnExit
      >
        <div className="page-loader">
          <Image name="preloader" ext="gif" />
        </div>
      </CSSTransition>
      <CSSTransition
        in={isPageReadyToShowContent}
        timeout={300}
        classNames="fade"
        unmountOnExit
        onEnter={() => setShowPreloader(false)}
        onExited={() => setShowPreloader(true)}
      >
        <>
          {/* don't show marketing in pre-rendered static pages */}
          {/* {navigator.userAgent !== "ReactSnap" && <MarketingPopupManager />} */}

          {/* don't show marketing in pre-rendered static pages */}
          {navigator.userAgent !== "ReactSnap" && <PopUpNotifications />}
          {/* add announcement to relevant page */}
          {navigator.userAgent !== "ReactSnap" && <Announcement />}
          {/* add metadata to relevant page */}
          {/* <MetaDataContent supportCategory={params.supportCategory} /> */}

          {/* page content/UI  */}
          <div
            className={`page-content ${directionClassName} ${
              pathname.includes("/form/") ? "script-form" : ""
            } ${navigator.userAgent === "ReactSnap" ? "hidden-content" : ""} ${
              pathname.includes("/inbox") ? "page-content-inbox" : ""
            } ${
              pathname.includes(CustomDomainPagesE.INVITE) ||
              (pathname.includes(CustomDomainPagesE.SUB_USER_COMPLETE) &&
                checkCurrDomainIsOrgDomain())
                ? "page-content-invite"
                : ""
            }`}
          >
            {(pathname.includes("/registration") &&
              !pathname.includes("incomplete") &&
              !pathname.includes("verification")) ||
            pathname.includes("/page/") ||
            pathname.includes("/form/") ||
            pathname.includes("/login") ||
            pathname.includes("/purchase-error-retry") ||
            (pathname.includes("/create-form") && isLoggedIn()) ||
            (pathname.includes("order-tracker") && search.includes("eid")) ||
            (pathname.includes("/page-edit") &&
              (isLoggedIn() || search.includes("dem="))) ? (
              <>
                {!pathname.includes("/form/") && <AccessibilityWidget />}
                {/* <CookiesUsageAlert /> */}
                {children}
                <ScrollToTop children="" />
              </>
            ) : (
              <>
                <AccessibilityWidget />
                {/* <CookiesUsageAlert /> */}
                {/* <SubscriptionExpirationReminder /> */}
                <BlackFridayHeader />
                {pathname.includes("inbox") ? null : (
                  <Header
                    showBlackFridayHeader=""
                    isCustomHeader={isCustomDomainPage() || hideWhenCollection}
                  />
                )}
                {children}
                {isCustomDomainPage() || hideWhenCollection ? (
                  <CustomDomainFooter />
                ) : (
                  <Footer children="" />
                )}
                <ScrollToTop children="" />
              </>
            )}
          </div>
          <PromoContactUsFloatingButton />
        </>
      </CSSTransition>
    </>
  );
};

RouterHelper.defaultProps = {
  params: {},
  support: false,
  options: {},
};
export default RouterHelper;
